/**
 * Générateur de descriptions d'événements à la demande
 * Remplace les descriptions HTML stockées dans le cache
 * Support étendu pour tous les types d'événements généalogiques
 */

import familyTownsStore from '../tabs/familyMap/stores/familyTownsStore.js';
import { EventRegistry } from './eventTypeRegistry.js';
import { getName, getSurname } from '../gedcom/formatters/individualFormatters.js';

/**
 * Génère une description enrichie pour les événements avec lookup dynamique
 * @param {Object} event - L'événement avec typeDescription
 * @param {Object} individual - L'individu (pour contexte)
 * @returns {string} - Description HTML formatée
 */
function generateEnrichedDescription(event, individual) {
    console.log(`📝 generateEnrichedDescription called with:`, event);
    const description = event.typeDescription || event.type;
    
    // Construire la description enrichie de base
    const baseParts = [description];
    
    // Ajouter les détails principaux selon le type
    switch (event.type) {
        case 'dh':
            // Distinction/Honneur
            if (event.m?.ct) baseParts.push(event.m.ct);
            break;
            
        case 'ad1':
        case 'ad2':
            // Adresse
            if (event.m?.v) baseParts.push(event.m.v);
            break;
            
        case 'pm':
            // Service militaire
            if (event.m?.ct) baseParts.push(event.m.ct);
            break;
            
        case 'po':
            // Profession
            if (event.m?.v) baseParts.push(event.m.v);
            break;
            
        case 'eg':
            // Graduation
            if (event.m?.et) baseParts.push(event.m.et);
            break;
            
        default:
            // Détails génériques
            if (event.m?.v) baseParts.push(event.m.v);
            if (event.m?.ct) baseParts.push(event.m.ct);
            break;
    }
    
    const baseDescription = baseParts.filter(Boolean).join(' : ');
    
    // Ajouter le bouton "Lire plus" si il y a des notes ou des détails supplémentaires
    const hasNotes = event.m?.n && event.m.n.length > 0;
    const hasExtraDetails = event.m?.sd; // Subdivision ou détails supplémentaires
    
    if (hasNotes || hasExtraDetails) {
        const noteId = hasNotes ? event.m.n[0] : null; // Première note
        const readMoreBtn = `<button class="btn btn-link btn-sm p-0 ms-2 read-more-btn" 
                                     data-note-id="${noteId || ''}" 
                                     data-extra-details="${hasExtraDetails ? event.m.sd : ''}"
                                     onclick="toggleEventDetails(this)">
                                📝 Lire plus
                             </button>`;
        return baseDescription + readMoreBtn;
    }
    
    return baseDescription;
}

/**
 * Utilitaire pour ajouter un bouton "Lire plus" si l'événement a des notes ou détails
 * @param {string} baseDescription - Description de base
 * @param {Object} event - L'événement
 * @returns {string} - Description avec bouton "Lire plus" si nécessaire
 */
function addReadMoreButtonIfNeeded(baseDescription, event) {
    const hasNotes = event.m?.n && event.m.n.length > 0;
    const hasExtraDetails = event.m?.sd || event.m?.et || event.m?.v || event.m?.ct;
    
    if (hasNotes || hasExtraDetails) {
        const noteId = hasNotes ? event.m.n[0] : null;
        const extraDetails = event.m?.sd || event.m?.et || event.m?.v || event.m?.ct || '';
        const readMoreBtn = `<button class="btn btn-link btn-sm p-0 ms-2 read-more-btn" 
                                     data-note-id="${noteId || ''}" 
                                     data-extra-details="${extraDetails}"
                                     onclick="toggleEventDetails(this)">
                                📝 Lire plus
                             </button>`;
        return baseDescription + readMoreBtn;
    }
    
    return baseDescription;
}

/**
 * Génère la description HTML d'un événement à la demande
 * @param {Object} event - L'événement
 * @param {Object} individual - L'individu (pour contexte)
 * @param {Map} individualsCache - Cache pour résoudre les liens
 * @returns {string} - Description HTML formatée
 */
export function generateEventDescription(event, individual, individualsCache = null) {
    if (!event) return '';

    // Si l'événement a une description enrichie (lookup dynamique), l'utiliser
    if (event.typeDescription) {
        return generateEnrichedDescription(event, individual);
    }
    
    // Vérifier si le type d'événement est supporté dans le registre statique
    if (!EventRegistry.isValidEventType(event.type)) {
        // Log uniquement en développement pour identifier les types manquants
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            console.warn(`Type d'événement non reconnu: ${event.type}`, event);
        }
        return generateGenericDescription(event, individual);
    }

    switch (event.type) {
        // === ÉVÉNEMENTS FAMILIAUX ===
        case 'birth':
            return generateBirthDescription(event);
        
        case 'death':
            return generateDeathDescription(event);
        
        case 'today':
            return generateTodayDescription(event, individual);
        
        case 'marriage':
            return generateMarriageDescription(event, individual, individualsCache);
        
        case 'divorce':
            return generateDivorceDescription(event, individual, individualsCache);
        
        case 'baptism':
            return generateBaptismDescription(event);
        
        case 'adoption':
            return generateAdoptionDescription(event);
        
        case 'child-birth':
            return generateChildBirthDescription(event, individual, individualsCache);

        // === ÉVÉNEMENTS PROFESSIONNELS ===
        case 'occupation':
            return generateOccupationDescription(event);
        
        case 'promotion':
            return generatePromotionDescription(event);
        
        case 'retirement':
            return generateRetirementDescription(event);
        
        case 'business-creation':
            return generateBusinessCreationDescription(event);

        // === ÉVÉNEMENTS ÉDUCATIFS ===
        case 'education':
            return generateEducationDescription(event);
        
        case 'diploma':
            return generateDiplomaDescription(event);
        
        case 'graduation':
            return generateGraduationDescription(event);
        
        case 'certification':
            return generateCertificationDescription(event);

        // === DISTINCTIONS ===
        case 'decoration':
            return generateDecorationDescription(event);
        
        case 'award':
            return generateAwardDescription(event);
        
        case 'recognition':
            return generateRecognitionDescription(event);
        
        case 'title':
            return generateTitleDescription(event);

        // === ÉVÉNEMENTS MÉDIATIQUES ===
        case 'media-mention':
            return generateMediaMentionDescription(event);
        
        case 'publication':
            return generatePublicationDescription(event);
        
        case 'interview':
            return generateInterviewDescription(event);
        
        case 'public-speech':
            return generatePublicSpeechDescription(event);

        // === ÉVÉNEMENTS GÉOGRAPHIQUES ===
        case 'migration':
            return generateMigrationDescription(event, individual);
        
        case 'residence':
            return generateResidenceDescription(event);
        
        case 'travel':
            return generateTravelDescription(event, individual);

        // === ÉVÉNEMENTS PERSONNELS ===
        case 'illness':
            return generateIllnessDescription(event);
        
        case 'recovery':
            return generateRecoveryDescription(event);
        
        case 'military-service':
            return generateMilitaryServiceDescription(event);
        
        default:
            return generateGenericDescription(event, individual);
    }
}

/**
 * Génère la description d'une naissance
 */
function generateBirthDescription(event) {
    // Phase 8.3: Utiliser townKey au lieu de town
    if (!event.townKey) {
        return ' à (lieu inconnu)';
    }

    // Récupérer les détails de la ville via le townKey
    const townData = familyTownsStore.getTownsData(event.townKey);
    if (!townData) {
        return ' à (lieu inconnu)';
    }
    
    const townDisplay = townData.departement ? `${townData.town} (${townData.departement})` : townData.town;
    
    return ` à <a href="#"><span class="city-link" data-town-key="${event.townKey}">${townDisplay}</span></a>`;
}

/**
 * Génère la description d'un décès
 */
function generateDeathDescription(event) {
    const age = event.age ? ` à ${event.age} ans` : '';
    
    // Phase 8.3: Utiliser townKey au lieu de town
    if (!event.townKey) {
        return `${age} à (lieu inconnu)`;
    }
    
    // Récupérer les détails de la ville via le townKey
    const townData = familyTownsStore.getTownsData(event.townKey);
    if (!townData) {
        return `${age} à (lieu inconnu)`;
    }
    
    const townDisplay = townData.departement ? `${townData.town} (${townData.departement})` : townData.town;
    
    return `${age} à <a href="#"><span class="city-link" data-town-key="${event.townKey}">${townDisplay}</span></a>`;
}

/**
 * Génère la description d'aujourd'hui (âge actuel)
 */
function generateTodayDescription(event, individual) {
    const age = event.age || (individual?.age ? `${individual.age} ans` : '');
    return `${age}  à (lieu inconnu)`;
}

/**
 * Génère la description d'un mariage
 */
function generateMarriageDescription(event, individual, individualsCache) {
    // Debug conditionnel - décommenter si besoin de déboguer les mariages
    // console.log('🔍 generateMarriageDescription:', { spouseId: event.spouseId });
    
    let spouseName = 'Conjoint inconnu';
    let spouseId = event.spouseId;
    
    // Récupérer le nom du conjoint depuis spouseId
    if (spouseId && individualsCache) {
        const spouse = individualsCache.get(spouseId);
        if (spouse) {
            const name = getName(spouse);
            const surname = getSurname(spouse);
            spouseName = `${name || ''} ${surname || ''}`.trim() || 'Conjoint inconnu';
        }
    }
    
    const spouseLink = spouseId ? 
        `<a href="#"><span class="person-link" data-person-id="${spouseId}">${spouseName}</span></a>` : 
        spouseName;
    
    // 🆕 Ajouter le type de mariage (civil/religieux) si disponible
    let marriageType = '';
    if (event.ceremonyType || event.m?.ct) {
        const type = event.ceremonyType || event.m?.ct;
        if (type === 'Civil' || type === 'civil') {
            marriageType = ' (mariage civil)';
        } else if (type === 'Religious' || type === 'religious' || type === 'religieux') {
            marriageType = ' (mariage religieux)';
        } else if (type) {
            marriageType = ` (${type})`;
        }
    }
    
    const ageText = event.age ? ` à ${event.age} ans` : '';
    
    // Phase 8.3: Utiliser townKey au lieu de town
    if (!event.townKey) {
        return `${marriageType} avec ${spouseLink}${ageText} à (lieu inconnu)`;
    }
    
    // Chercher le département dans les villes de l'individu
    let department = '';
    if (individual?.t && Array.isArray(individual.t)) {
        // Phase 8.3: event.townKey est déjà normalisé
        if (individual.t.includes(event.townKey)) {
            // Récupérer les détails depuis familyTownsStore
            const townData = familyTownsStore.getTownsData(event.townKey);
            // department = townData?.departement || ''; // Unused variable
        }
    }
    
    // Récupérer les détails de la ville via le townKey
    const townData = familyTownsStore.getTownsData(event.townKey);
    const townDisplay = townData?.departement ? `${townData.town} (${townData.departement})` : `${townData?.town || event.townKey} (pays inconnu)`;
    const townKey = event.townKey;
    
    return `${marriageType} avec ${spouseLink}${ageText} à <a href="#"><span class="city-link" data-town-key="${townKey}">${townDisplay}</span></a>`;
}

/**
 * Génère la description d'une naissance d'enfant
 */
function generateChildBirthDescription(event, individual, individualsCache) {
    // Utiliser le childId stocké dans l'événement optimisé
    const childId = event.childId;
    let childName = 'Enfant inconnu';
    
    // Récupérer le nom de l'enfant via son ID si disponible
    if (childId && individualsCache) {
        const child = individualsCache.get(childId);
        if (child) {
            const name = getName(child) || '';
            const surname = getSurname(child) || '';
            childName = `${name} ${surname}`.trim() || 'Enfant inconnu';
        }
    } else if (event.name) {
        // Fallback: utiliser le nom s'il est présent (ancienne structure)
        childName = event.name;
    }
    
    const childLink = childId ? 
        `<a href="#"><span class="person-link" data-person-id="${childId}">${childName}</span></a>` : 
        `<a href="#"><span class="person-link" data-person-id="">${childName}</span></a>`;
    
    return childLink;
}

/**
 * Génère une description générique
 */
function generateGenericDescription(event, individual) {
    const ageText = event.age ? ` à ${event.age} ans` : '';
    
    // Phase 8.3: Utiliser townKey au lieu de town
    if (!event.townKey) {
        return `${ageText} à (lieu inconnu)`;
    }
    
    // Récupérer les détails de la ville via le townKey
    const townData = familyTownsStore.getTownsData(event.townKey);
    const townName = townData?.town || event.townKey;
    
    return `${ageText} à ${townName}`;
}

// Phase 8.3: normalizeForKey supprimée - on utilise directement townKey

/**
 * Génère toutes les descriptions d'événements pour un individu
 * @param {Array} events - Liste des événements
 * @param {Object} individual - L'individu
 * @param {Map} individualsCache - Cache des individus
 * @returns {Array} - Événements avec descriptions générées
 */
export function generateAllEventDescriptions(events, individual, individualsCache = null) {
    if (!Array.isArray(events)) return [];
    
    return events.map(event => ({
        ...event,
        description: generateEventDescription(event, individual, individualsCache)
    }));
}

/**
 * Wrapper pour la compatibilité avec personDetailsDisplay.js
 */
export function getEventsWithDescriptions(individual, individualsCache = null) {
    if (!individual?.e) return [];
    
    return generateAllEventDescriptions(individual.e, individual, individualsCache);
}

// === NOUVELLES FONCTIONS DE GÉNÉRATION DE DESCRIPTIONS ===

/**
 * Génère la description d'un divorce
 */
function generateDivorceDescription(event, individual, individualsCache) {
    let spouseName = 'Ex-conjoint inconnu';
    let spouseId = null;
    
    if (event.eventAttendees) {
        spouseId = event.eventAttendees;
        if (spouseId && individualsCache) {
            const spouse = individualsCache.get(spouseId);
            if (spouse) {
                spouseName = `${getName(spouse) || ''} ${getSurname(spouse) || ''}`.trim();
            }
        }
    }
    
    const spouseLink = spouseId ? 
        `<a href="#"><span class="person-link" data-person-id="${spouseId}">${spouseName}</span></a>` : 
        spouseName;
    
    const locationText = formatLocation(event.townKey);
    return ` avec ${spouseLink}${locationText}`;
}

/**
 * Génère la description d'un baptême
 */
function generateBaptismDescription(event) {
    let description = '';
    
    if (event.church) {
        description += ` en l'église ${event.church}`;
    }
    
    if (event.eventAttendees) {
        description += ` (parrains/marraines: ${event.eventAttendees})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une adoption
 */
function generateAdoptionDescription(event) {
    let description = '';
    
    if (event.eventAttendees) {
        description = ` (${event.eventAttendees})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une profession
 */
function generateOccupationDescription(event) {
    let description = '';
    const parts = [];

    // 🆕 Phase 6.5: Profession (métier)
    if (event.profession || event.m?.pf) {
        parts.push(event.profession || event.m.pf);
    }

    // 🆕 Phase 6.5: Position (fonction)
    if (event.position || event.m?.ps) {
        parts.push(event.position || event.m.ps);
    }

    // Backward compat: fallback to legacy
    if (parts.length === 0 && (event.occupation || event.m?.v)) {
        parts.push(event.occupation || event.m.v);
    }

    // USER CHOICE: Comma-separated format "Ingénieur, Directeur"
    if (parts.length > 0) {
        description = ` - ${parts.join(', ')}`;
    }

    // Add organization
    if (event.organization || event.m?.o) {
        description += ` chez ${event.organization || event.m.o}`;
    } else if (event.employer) {
        description += ` chez ${event.employer}`;
    }

    const locationText = formatLocation(event.townKey, false);
    const baseDescription = `${description}${locationText}`;

    return addReadMoreButtonIfNeeded(baseDescription, event);
}

/**
 * Génère la description d'une promotion
 */
function generatePromotionDescription(event) {
    let description = '';
    
    if (event.newPosition) {
        description = ` - ${event.newPosition}`;
    }
    
    if (event.employer) {
        description += ` chez ${event.employer}`;
    }
    
    const locationText = formatLocation(event.townKey, false);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une retraite
 */
function generateRetirementDescription(event) {
    let description = '';
    
    if (event.employer) {
        description = ` de ${event.employer}`;
    }
    
    const locationText = formatLocation(event.townKey, false);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une création d'entreprise
 */
function generateBusinessCreationDescription(event) {
    let description = '';
    
    if (event.businessName) {
        description = ` - ${event.businessName}`;
    }
    
    if (event.businessType) {
        description += ` (${event.businessType})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'éducation
 */
function generateEducationDescription(event) {
    let description = '';
    
    if (event.institution) {
        description = ` à ${event.institution}`;
    }
    
    if (event.degree) {
        description += ` (${event.degree})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'un diplôme
 */
function generateDiplomaDescription(event) {
    let description = '';
    
    if (event.diplomaName) {
        description = ` - ${event.diplomaName}`;
    }
    
    if (event.institution) {
        description += ` (${event.institution})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une remise de diplôme
 */
function generateGraduationDescription(event) {
    let description = '';
    
    if (event.institution) {
        description = ` de ${event.institution}`;
    }
    
    if (event.degree || event.eventType) {
        description += ` (${event.degree || event.eventType})`;
    }
    
    const locationText = formatLocation(event.townKey);
    const baseDescription = `${description}${locationText}`;
    
    return addReadMoreButtonIfNeeded(baseDescription, event);
}

/**
 * Génère la description d'une certification
 */
function generateCertificationDescription(event) {
    let description = '';
    
    if (event.certificationName) {
        description = ` - ${event.certificationName}`;
    }
    
    if (event.organization) {
        description += ` (${event.organization})`;
    }
    
    return description;
}

/**
 * Génère la description d'une décoration
 */
function generateDecorationDescription(event) {
    let description = '';
    
    if (event.name || event.decorationName) {
        description = ` - ${event.name || event.decorationName}`;
    }
    
    if (event.authority) {
        description += ` (${event.authority})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une récompense
 */
function generateAwardDescription(event) {
    let description = '';
    
    if (event.awardName) {
        description = ` - ${event.awardName}`;
    }
    
    if (event.organization) {
        description += ` (${event.organization})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une reconnaissance
 */
function generateRecognitionDescription(event) {
    let description = '';
    
    if (event.recognitionType) {
        description = ` - ${event.recognitionType}`;
    }
    
    if (event.authority) {
        description += ` par ${event.authority}`;
    }
    
    return description;
}

/**
 * Génère la description d'un titre
 */
function generateTitleDescription(event) {
    let description = '';
    
    if (event.name || event.titleName) {
        description = ` - ${event.name || event.titleName}`;
    }
    
    if (event.authority) {
        description += ` (${event.authority})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une mention médiatique
 */
function generateMediaMentionDescription(event) {
    let description = '';
    
    if (event.mediaType) {
        description = ` - ${event.mediaType}`;
    }
    
    if (event.publication) {
        description += ` (${event.publication})`;
    }
    
    return description;
}

/**
 * Génère la description d'une publication
 */
function generatePublicationDescription(event) {
    let description = '';
    
    if (event.publicationName) {
        description = ` - ${event.publicationName}`;
    }
    
    if (event.publisher) {
        description += ` (${event.publisher})`;
    }
    
    return description;
}

/**
 * Génère la description d'une interview
 */
function generateInterviewDescription(event) {
    let description = '';
    
    if (event.interviewer) {
        description = ` avec ${event.interviewer}`;
    }
    
    if (event.media) {
        description += ` (${event.media})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'un discours public
 */
function generatePublicSpeechDescription(event) {
    let description = '';
    
    if (event.occasion) {
        description = ` - ${event.occasion}`;
    }
    
    if (event.audience) {
        description += ` devant ${event.audience}`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une migration
 */
function generateMigrationDescription(event, individual) {
    let description = '';
    
    if (event.originKey && event.destinationKey) {
        const originData = familyTownsStore.getTownsData(event.originKey);
        const destData = familyTownsStore.getTownsData(event.destinationKey);
        
        const origin = originData?.town || event.originKey;
        const destination = destData?.town || event.destinationKey;
        
        description = ` de ${origin} vers ${destination}`;
    } else if (event.destinationKey) {
        const destData = familyTownsStore.getTownsData(event.destinationKey);
        const destination = destData?.town || event.destinationKey;
        description = ` vers ${destination}`;
    }
    
    if (event.reason) {
        description += ` (${event.reason})`;
    }
    
    return description;
}

/**
 * Génère la description d'une résidence
 */
function generateResidenceDescription(event) {
    let description = '';
    
    if (event.address) {
        description = ` - ${event.address}`;
    }
    
    if (event.propertyType) {
        description += ` (${event.propertyType})`;
    }
    
    const locationText = formatLocation(event.townKey);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'un voyage
 */
function generateTravelDescription(event, individual) {
    let description = '';
    
    if (event.destinationKey) {
        const destData = familyTownsStore.getTownsData(event.destinationKey);
        const destination = destData?.town || event.destinationKey;
        description = ` vers ${destination}`;
    }
    
    if (event.purpose) {
        description += ` (${event.purpose})`;
    }
    
    return description;
}

/**
 * Génère la description d'une maladie
 */
function generateIllnessDescription(event) {
    let description = '';
    
    if (event.illness) {
        description = ` - ${event.illness}`;
    }
    
    if (event.doctor) {
        description += ` (Dr ${event.doctor})`;
    }
    
    const locationText = formatLocation(event.townKey, false);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'une guérison
 */
function generateRecoveryDescription(event) {
    let description = '';
    
    if (event.illness) {
        description = ` de ${event.illness}`;
    }
    
    if (event.treatment) {
        description += ` (${event.treatment})`;
    }
    
    const locationText = formatLocation(event.townKey, false);
    return `${description}${locationText}`;
}

/**
 * Génère la description d'un service militaire
 */
function generateMilitaryServiceDescription(event) {
    let description = '';
    
    if (event.regiment) {
        description = ` - ${event.regiment}`;
    }
    
    if (event.rank) {
        description += ` (${event.rank})`;
    }
    
    const locationText = formatLocation(event.townKey);
    const baseDescription = `${description}${locationText}`;
    
    return addReadMoreButtonIfNeeded(baseDescription, event);
}

/**
 * Utilitaire pour formater l'affichage des lieux
 * @param {string} townKey - Clé normalisée de la ville
 * @param {boolean} usePrefix - Utiliser le préfixe "à" (défaut: true)
 * @returns {string} - Texte formaté du lieu
 */
function formatLocation(townKey, usePrefix = true) {
    if (!townKey) {
        return usePrefix ? ' à (lieu inconnu)' : '';
    }

    const townData = familyTownsStore.getTownsData(townKey);
    if (!townData) {
        return usePrefix ? ' à (lieu inconnu)' : '';
    }
    
    const townDisplay = townData.departement ? 
        `${townData.town} (${townData.departement})` : 
        townData.town;
    
    const prefix = usePrefix ? ' à ' : ' ';
    return `${prefix}<a href="#"><span class="city-link" data-town-key="${townKey}">${townDisplay}</span></a>`;
}

